/**
 * Event Definitions List Component
 *
 * Displays a list of notification event definitions for a merchant.
 * Allows creating, editing, and deleting event definitions.
 */

import React, { useEffect, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import { confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { Eye, Pencil, Trash2, Ban, CheckCircle } from "lucide-react";
import Loader from "../../components/Loader";
import { eventDefinitionsApiService } from "../../services/api/notifications";
import {
    EventDefinitionDto,
    parseAllowedChannels,
    NotificationChannelLabels,
    NotificationPriority,
    NotificationPriorityLabels,
} from "../../models/notifications";
import EventDefinitionEditor from "./EventDefinitionEditor";

interface EventDefinitionsProps {
    merchantId: number;
    onEventSelect?: (eventCode: string) => void;
}

const EventDefinitions: React.FC<EventDefinitionsProps> = ({ merchantId, onEventSelect }) => {
    const toast = useRef<Toast>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [events, setEvents] = useState<EventDefinitionDto[]>([]);
    const [filteredEvents, setFilteredEvents] = useState<EventDefinitionDto[]>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingEvent, setEditingEvent] = useState<EventDefinitionDto | null>(null);

    useEffect(() => {
        loadEvents();
    }, [merchantId, showInactive]);

    useEffect(() => {
        filterEvents();
    }, [events, globalFilter]);

    const loadEvents = async () => {
        setIsLoading(true);
        const result = await eventDefinitionsApiService.getAll(merchantId, showInactive);
        if (result.success) {
            setEvents(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to load event definitions",
                life: 4000,
            });
        }
        setIsLoading(false);
    };

    const filterEvents = () => {
        if (!globalFilter.trim()) {
            setFilteredEvents(events);
            return;
        }

        const filter = globalFilter.toLowerCase();
        const filtered = events.filter(
            (e) =>
                e.eventCode.toLowerCase().includes(filter) ||
                e.description?.toLowerCase().includes(filter) ||
                e.allowedChannels.some((ch) =>
                    NotificationChannelLabels[ch]?.toLowerCase().includes(filter)
                )
        );
        setFilteredEvents(filtered);
    };

    const openCreate = () => {
        setEditingEvent(null);
        setEditorVisible(true);
    };

    const openEdit = (event: EventDefinitionDto) => {
        setEditingEvent(event);
        setEditorVisible(true);
    };

    const handleEditorSave = () => {
        setEditorVisible(false);
        setEditingEvent(null);
        loadEvents();
    };

    const handleDelete = (event: EventDefinitionDto) => {
        confirmDialog({
            message: `Are you sure you want to delete the event "${event.eventCode}"? This action cannot be undone.`,
            header: "Delete Event Definition",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const result = await eventDefinitionsApiService.delete(merchantId, event.eventCode);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: `Event "${event.eventCode}" has been deleted`,
                        life: 3000,
                    });
                    loadEvents();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to delete event definition",
                        life: 4000,
                    });
                }
            },
        });
    };

    const handleToggleActive = async (event: EventDefinitionDto) => {
        const result = event.isActive
            ? await eventDefinitionsApiService.deactivate(merchantId, event.eventCode)
            : await eventDefinitionsApiService.activate(merchantId, event.eventCode);

        if (result.success) {
            toast.current?.show({
                severity: "success",
                summary: "Updated",
                detail: `Event "${event.eventCode}" has been ${event.isActive ? "deactivated" : "activated"}`,
                life: 3000,
            });
            loadEvents();
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to update event status",
                life: 4000,
            });
        }
    };

    // Column templates
    const eventCodeTemplate = (row: EventDefinitionDto) => (
        <span className="font-mono font-semibold text-blue-600">{row.eventCode}</span>
    );

    const channelsTemplate = (row: EventDefinitionDto) => {
        const channels = parseAllowedChannels(row.allowedChannels);
        return (
            <div className="flex flex-wrap gap-1">
                {channels.map((ch) => (
                    <Tag
                        key={ch}
                        value={NotificationChannelLabels[ch]}
                        severity="info"
                        className="text-xs"
                    />
                ))}
            </div>
        );
    };

    const variablesTemplate = (row: EventDefinitionDto) => {
        const vars = row.allowedVariables ?? [];
        if (vars.length === 0) {
            return <span className="text-gray-400 italic">None</span>;
        }
        return (
            <div className="flex flex-wrap gap-1">
                {vars.slice(0, 3).map((v) => (
                    <Tag key={v} value={`{{${v}}}`} className="text-xs bg-slate-100 text-slate-700" />
                ))}
                {vars.length > 3 && (
                    <Tag value={`+${vars.length - 3} more`} className="text-xs bg-slate-200 text-slate-600" />
                )}
            </div>
        );
    };

    const priorityTemplate = (row: EventDefinitionDto) => {
        const priority = row.priority ?? NotificationPriority.Normal;
        return (
            <Tag
                value={NotificationPriorityLabels[priority] ?? "Normal"}
                severity={priority === NotificationPriority.High ? "danger" : "info"}
            />
        );
    };

    const statusTemplate = (row: EventDefinitionDto) => (
        <Tag
            value={row.isActive ? "Active" : "Inactive"}
            severity={row.isActive ? "success" : "warning"}
        />
    );

    const actionsTemplate = (row: EventDefinitionDto) => (
        <div className="flex items-center gap-1">
            {onEventSelect && (
                <button
                    type="button"
                    onClick={() => onEventSelect(row.eventCode)}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                    title="View Templates"
                >
                    <Eye size={15} />
                </button>
            )}
            <button
                type="button"
                onClick={() => openEdit(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            <button
                type="button"
                onClick={() => handleToggleActive(row)}
                className={`p-1.5 rounded-md transition-colors ${row.isActive
                    ? "text-neutral-400 hover:text-warning-600 hover:bg-warning-50 dark:hover:text-warning-400 dark:hover:bg-warning-900/20"
                    : "text-neutral-400 hover:text-success-600 hover:bg-success-50 dark:hover:text-success-400 dark:hover:bg-success-900/20"
                }`}
                title={row.isActive ? "Deactivate" : "Activate"}
            >
                {row.isActive ? <Ban size={15} /> : <CheckCircle size={15} />}
            </button>
            <button
                type="button"
                onClick={() => handleDelete(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Delete"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );

    const header = (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search events..."
                        className="w-64 !pl-10"
                    />
                </span>
                <div className="flex items-center gap-2">
                    <InputSwitch
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.value ?? false)}
                    />
                    <span className="text-sm text-gray-600">Show inactive</span>
                </div>
            </div>
            <Button
                size="small"
                label="Create Event"
                icon="pi pi-plus"
                onClick={openCreate}
            />
        </div>
    );

    if (isLoading && events.length === 0) {
        return <Loader />;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <AppCard>
                <AppCard.Header>
                    <div className="flex flex-col gap-1">
                        <span>Event Definitions</span>
                        <small className="text-neutral-500 font-normal">
                            Define the types of notifications that can be sent for this merchant.
                        </small>
                    </div>
                </AppCard.Header>
                <AppCard.Body>
                    <DataTable
                        value={filteredEvents}
                        loading={isLoading}
                        header={header}
                        emptyMessage="No event definitions found"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        stripedRows
                        pt={{
                            header: { className: "border-gray-200 rounded-t-lg" },
                            wrapper: { className: "border-gray-200 border-x border-b" },
                            bodyRow: { className: "even:!bg-blue-50/50" },
                        }}
                    >
                        <Column
                            field="eventCode"
                            header="Event Code"
                            body={eventCodeTemplate}
                            sortable
                            style={{ minWidth: "200px" }}
                        />
                        <Column
                            field="description"
                            header="Description"
                            style={{ minWidth: "250px" }}
                        />
                        <Column
                            header="Channels"
                            body={channelsTemplate}
                            style={{ minWidth: "180px" }}
                        />
                        <Column
                            header="Variables"
                            body={variablesTemplate}
                            style={{ minWidth: "200px" }}
                        />
                        <Column
                            header="Priority"
                            body={priorityTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Status"
                            body={statusTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ width: "180px" }}
                        />
                    </DataTable>
                </AppCard.Body>
            </AppCard>

            {editorVisible && (
                <EventDefinitionEditor
                    merchantId={merchantId}
                    event={editingEvent}
                    onSave={handleEditorSave}
                    onCancel={() => {
                        setEditorVisible(false);
                        setEditingEvent(null);
                    }}
                />
            )}
        </>
    );
};

export default EventDefinitions;
